import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Checkbox, FormControlLabel } from '@mui/material';
import { Card, TextField } from '@totalsoft/rocket-ui';
import Income from '@mui/icons-material/PlaylistAddCheck';
import { useTranslation } from 'react-i18next';
import { get, set } from '@totalsoft/change-tracking-react';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import IconButton from 'components/common/buttons/IconButton';
import { usePermissions } from 'providers/RightsHooks';
import { FlowPermissionType, useFlowPermissions } from './hooks';

const GenericEntityFlowEditGeneralDataCard = ({ genericEntityFlowLens, validation, onSave, isDirty, headerSaving }) => {
  const { t } = useTranslation();

  const authorizationCode = useFlowPermissions(FlowPermissionType.WRITE);
  const canWrite = usePermissions(authorizationCode);
  return (
    <Card
      icon={Income}
      iconColor='primary'
      title={t('GenericEntityFlow.Title')}
      actions={
        <IconButton
          type='save'
          disabled={!canWrite || !isDirty || headerSaving || getErrors(validation) !== ''}
          onClick={() => {
            onSave(genericEntityFlowLens);
          }}
        />
      }
    >
      {genericEntityFlowLens && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              value={genericEntityFlowLens.flowCode |> get}
              onChange={genericEntityFlowLens.flowCode |> set}
              label={t('GenericEntityFlow.Code')}
              error={!isValid(validation.flowCode)}
              helperText={getErrors(validation.flowCode)}
              disabled={!canWrite}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              value={genericEntityFlowLens.flowName |> get}
              onChange={genericEntityFlowLens.flowName |> set}
              label={t('GenericEntityFlow.Name')}
              error={!isValid(validation.flowName)}
              helperText={getErrors(validation.flowName)}
              disabled={!canWrite}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={genericEntityFlowLens.enabledYN |> get}
                  onChange={genericEntityFlowLens.enabledYN |> set |> onCheckBoxChange}
                  color='primary'
                />
              }
              label={t('GenericEntityFlow.IsActive')}
              disabled={!canWrite}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              value={genericEntityFlowLens.preConditionProcedure |> get}
              onChange={genericEntityFlowLens.preConditionProcedure |> set}
              label={t('GenericEntityFlow.BeforeProcedure')}
              disabled={!canWrite}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              value={genericEntityFlowLens.postConditionProcedure |> get}
              onChange={genericEntityFlowLens.postConditionProcedure |> set}
              label={t('GenericEntityFlow.AfterProcedure')}
              disabled={!canWrite}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={genericEntityFlowLens.mainFlow |> get}
                  onChange={genericEntityFlowLens.mainFlow |> set |> onCheckBoxChange}
                  color='primary'
                />
              }
              label={t('GenericEntityFlow.PrincipalFlow')}
              disabled={!canWrite}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <TextField
              fullWidth
              id='outlined-multiline-static'
              label={t('GenericEntityFlow.Comments')}
              onChange={genericEntityFlowLens.freeTxt |> set}
              multiline
              value={genericEntityFlowLens.freeTxt |> get}
              disabled={!canWrite}
            />
          </Grid>
        </Grid>
      )}
    </Card>
  );
};

GenericEntityFlowEditGeneralDataCard.propTypes = {
  genericEntityFlowLens: PropTypes.object.isRequired,
  validation: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  isDirty: PropTypes.bool.isRequired,
  headerSaving: PropTypes.bool.isRequired
};

export default React.memo(GenericEntityFlowEditGeneralDataCard);
